/**
 * CR138: Hook to fetch the active layout preset for a given environment
 * CR150: Added cache-busting support and force-refresh capability
 * 
 * This is a lightweight hook for consuming activated layout presets
 * (separate from useLayoutStudio which is for admin CRUD operations).
 * 
 * Used by Live Preview and public-facing pages to render the correct layout.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { LayoutJson, LayoutPreset } from "@/lib/layout-studio/types";
import { DEFAULT_LAYOUT_JSON } from "@/lib/layout-studio/types";

export type LayoutEnvironment = "staging" | "publish";

export interface ActiveLayoutResult {
  layout: LayoutJson;
  preset: LayoutPreset | null;
  loading: boolean;
  syncing: boolean;
  error: string | null;
  lastSynced: string | null;
  /** CR150: Force refetch with cache-busting */
  refetch: () => Promise<void>;
  /** CR150: Sync layout with cache-bust (for user-initiated sync) */
  syncLayout: () => Promise<boolean>;
}

/**
 * Fetch the active layout preset for a given environment.
 * Returns the layout JSON and metadata about the active preset.
 */
export function useActiveLayout(environment: LayoutEnvironment): ActiveLayoutResult {
  const [preset, setPreset] = useState<LayoutPreset | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSynced, setLastSynced] = useState<string | null>(null);
  
  // CR150: Cache-bust counter to force fresh fetches
  const cacheBustRef = useRef(0);

  const fetchActiveLayout = useCallback(async (forceCacheBust = false) => {
    const isInitialLoad = cacheBustRef.current === 0;
    
    if (isInitialLoad) {
      setLoading(true);
    } else if (forceCacheBust) {
      setSyncing(true);
    }
    
    setError(null);

    try {
      // CR150: Increment cache-bust counter
      if (forceCacheBust) {
        cacheBustRef.current += 1;
      }

      // 1. Get activation record for environment
      // CR150: Use cache-busting header pattern
      const { data: activation, error: activationError } = await supabase
        .from("deal_finder_layout_activations")
        .select("active_preset_id, updated_at")
        .eq("environment", environment)
        .maybeSingle();

      if (activationError) {
        console.error("[useActiveLayout] Activation query error:", activationError);
        setError(activationError.message);
        setLoading(false);
        setSyncing(false);
        return;
      }

      if (!activation?.active_preset_id) {
        // No preset activated for this environment
        console.log(`[useActiveLayout] No active preset for ${environment}`);
        setPreset(null);
        setLastSynced(null);
        setLoading(false);
        setSyncing(false);
        return;
      }

      // 2. Fetch the preset details
      const { data: presetData, error: presetError } = await supabase
        .from("deal_finder_layout_presets")
        .select("*")
        .eq("id", activation.active_preset_id)
        .single();

      if (presetError) {
        console.error("[useActiveLayout] Preset query error:", presetError);
        setError(presetError.message);
        setLoading(false);
        setSyncing(false);
        return;
      }

      // Cast the database response to our typed interface
      const typedPreset = {
        ...presetData,
        layout_json: presetData.layout_json as unknown as LayoutJson,
        status: presetData.status as LayoutPreset["status"],
      } as LayoutPreset;
      
      setPreset(typedPreset);
      // CR150: Update lastSynced to NOW when force-refreshing
      setLastSynced(forceCacheBust ? new Date().toISOString() : (activation.updated_at || presetData.updated_at));
      
      console.log(`[useActiveLayout] Loaded preset for ${environment}:`, presetData.name, forceCacheBust ? "(cache-busted)" : "");
    } catch (err) {
      console.error("[useActiveLayout] Unexpected error:", err);
      setError(String(err));
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  }, [environment]);

  // Fetch on mount and when environment changes
  useEffect(() => {
    // Reset cache-bust counter when environment changes
    cacheBustRef.current = 0;
    fetchActiveLayout(false);
  }, [fetchActiveLayout]);

  // CR150: Sync layout with cache-bust (user-initiated)
  const syncLayout = useCallback(async (): Promise<boolean> => {
    try {
      await fetchActiveLayout(true);
      return true;
    } catch {
      return false;
    }
  }, [fetchActiveLayout]);

  // Derive layout from preset
  const layout: LayoutJson = preset?.layout_json || DEFAULT_LAYOUT_JSON;

  return {
    layout,
    preset,
    loading,
    syncing,
    error,
    lastSynced,
    refetch: () => fetchActiveLayout(true),
    syncLayout,
  };
}
